import { NgModule } from '@angular/core';
import { AppButtonModule } from 'src/app/components/button/button.module';

import { CommonModule } from '@angular/common';
import { ToastrModule } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

/** Component */
import { AppComponent } from './app.component';
import { AppLayout } from './layouts/app/app.layout';
import { AuthLayout } from './layouts/auth/auth.layout';

import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { provideAnalytics, getAnalytics } from '@angular/fire/analytics';

/** Modules */
import { AppLayoutModule } from './layouts/app/app.layout.module';
import { AuthLayoutModule } from './layouts/auth/auth.layout.module';

/** 3rd party modules */
import { NgSelectModule } from '@ng-select/ng-select';

/** Interceptors */
import { CustomHttpInterceptor } from './interceptors/custom-http.interceptor';
import { GLOBAL_INPUT_TRIM_PROVIDER } from './utils/global-input-trim';

/** Guards*/
import { AuthGuard } from './guards/auth.guard';
import { ConfigGuard } from './guards/config.guard';
import { ContextGuard } from './guards/context.guard';
import { IfNotLoggedInGuard } from './guards/if-not-logged-in.guard';
import { AttachmentsListComponentModule } from './components/attachments-list/attachments-list.component.module';
import { firebaseConfig } from 'src/environments/environment';
import { PermissionGuard } from './guards/permission.guard';
import { RouteAccessGuard } from './guards/route-access.guard';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { SupportIconComponent } from './components/support-icon/support-icon.component';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';

export function getDatepickerConfig(): BsDatepickerConfig {
  const config = new BsDatepickerConfig();

  config.dateInputFormat = 'MMMM DD YYYY';
  config.containerClass = 'theme-dark-blue';
  config.showWeekNumbers = false;
  return config;
}


@NgModule({
  declarations: [AppComponent],
  imports: [
    AppButtonModule,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    HttpClientModule,
    AppLayoutModule,
    AuthLayoutModule,
    NgSelectModule,
    FormsModule,
    AttachmentsListComponentModule,
    SpinnerComponent,
    SupportIconComponent,
    BsDatepickerModule.forRoot(),
    // TranslateModule.forRoot(),
    ToastrModule.forRoot(),
    TooltipModule.forRoot(),
    RouterModule.forRoot([
      {
        path: '',
        component: AuthLayout,
        canActivate: [AuthGuard],
        children: [
          {
            path: '',
            canActivate: [ContextGuard],
            canActivateChild: [RouteAccessGuard],
            children: [
              {
                path: '',
                loadChildren: () =>
                  import('./pages/dashboard/dashboard.module').then(
                    (m) => m.DashboardModule
                  ),
              },
              {
                path: 'meal-dynamics-ai',
                loadChildren: () =>
                  import('./pages/meal-dynamics-ai/meal-dynamics-ai.module').then(
                    (m) => m.MealDynamicsAiModule
                  ),
              },
              {
                path: 'companies',
                loadChildren: () =>
                  import('./pages/companies/companies.module').then(
                    (m) => m.CompaniesModule
                  ),
              },
              {
                path: 'company',
                loadChildren: () =>
                  import('./pages/companies/companies.module').then(
                    (m) => m.CompaniesModule
                  ),
              },
              {
                path: 'stores',
                loadChildren: () =>
                  import('./pages/stores/stores.module').then(
                    (m) => m.StoresModule
                  ),
              },
              {
                path: 'store',
                loadChildren: () =>
                  import('./pages/stores/stores.module').then(
                    (m) => m.StoresModule
                  ),
              },
              {
                path: 'approval-processes',
                loadChildren: () =>
                  import('./pages/approval-process/approval-process.module').then(
                    (m) => m.ApprovalProcessModule
                  ),
              },
              {
                path: 'approval-process',
                loadChildren: () =>
                  import('./pages/approval-process/approval-process.module').then(
                    (m) => m.ApprovalProcessModule
                  ),
              },
              {
                path: 'repairs-and-maintenance',
                loadChildren: () =>
                  import('./pages/repairs-and-maintenance/repairs-and-maintenance.module').then(
                    (m) => m.RepairsAndMaintenanceModule
                  ),
              },
              {
                path: 'users',
                loadChildren: () =>
                  import('./pages/users/users.module').then(
                    (m) => m.UsersModule
                  ),
              },
              {
                path: 'roles',
                loadChildren: () =>
                  import('./pages/roles/roles.module').then(
                    (m) => m.RolesModule
                  ),
              },
              {
                path: 'vendors',
                loadChildren: () =>
                  import('./pages/venders/venders.module').then(
                    (m) => m.VendersModule
                  ),
              },
              {
                path: 'vendor',
                loadChildren: () =>
                  import('./pages/venders/venders.module').then(
                    (m) => m.VendersModule
                  ),
              },
              {
                path: 'profile',
                loadChildren: () =>
                  import('./pages/profile/profile.module').then(
                    (m) => m.ProfileModule
                  ),
              },
              {
                path: 'expenses',
                loadChildren: () =>
                  import('./pages/expenses/expenses.module').then(
                    (m) => m.ExpensesModule
                  ),
              },
              {
                path: 'accounts-payable',
                loadChildren: () =>
                  import('./pages/account-payable/account-payable.module').then(
                    (m) => m.AccountPayableModule
                  ),
              },
              {
                path: 'asset-management',
                loadChildren: () =>
                  import('./pages/assets-managements/asset-management.module').then(
                    (m) => m.AssetManagementModule
                  ),
              },
              {
                path: 'notifications',
                loadChildren: () =>
                  import('./pages/notifications/notifications.module').then(
                    (m) => m.NotificationsModule
                  ),
              },
              {
                path: 'ai-preferences',
                loadChildren: () =>
                  import('./pages/ai-preferences/ai-preferences.module').then(
                    (m) => m.AiPreferencesModule
                  ),
              },
              {
                path: 'integration-data',
                loadChildren: () =>
                  import('./pages/integration-data/integration-data.module').then(
                    (m) => m.IntegrationDataModule
                  ),
              },
              {
                path: 'announcements',
                loadChildren: () =>
                  import('./pages/announcements/announcements.module').then(
                    (m) => m.AnnouncementsModule
                  ),
              },
              {
                path: 'support',
                loadChildren: () =>
                  import('./pages/support/support.module').then(
                    (m) => m.SupportModule
                  ),
              },
              {
                path: 'store-groups',
                loadChildren: () =>
                  import('./pages/store-groups/store-groups.module').then(
                    (m) => m.StoreGroupsModule
                  ),
              },
              {
                path: 'store-group',
                loadChildren: () =>
                  import('./pages/store-groups/store-groups.module').then(
                    (m) => m.StoreGroupsModule
                  ),
              },
            ],
          },
        ],
      },
      {
        // Dedicated email "Grant access" link: opened by the mailbox owner, signed in or not.
        path: 'mailbox',
        loadChildren: () =>
          import('./pages/mailbox-connect/mailbox-connect.module').then(
            (m) => m.MailboxConnectModule
          ),
      },
      {
        path: 'login',
        canActivate: [IfNotLoggedInGuard],
        loadChildren: () =>
          import('./pages/login/login.module').then(
            (m) => m.LoginModule
          ),
      },
      {
        path: '**',
        pathMatch: 'full',
        loadChildren: () =>
          import('./pages/error/error.page.module').then(
            (m) => m.ErrorPageModule
          ),
      },
    ], { scrollPositionRestoration: 'top' }),

  ],
  providers: [
    provideFirebaseApp(() => initializeApp(firebaseConfig)),
    provideAnalytics(() => getAnalytics()),
    provideMessaging(() => getMessaging()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHttpInterceptor,
      multi: true
    },
    { provide: BsDatepickerConfig, useFactory: getDatepickerConfig },
    GLOBAL_INPUT_TRIM_PROVIDER,
    ConfigGuard,
    ContextGuard,
    AuthGuard,
    IfNotLoggedInGuard,
  ],


  bootstrap: [AppComponent],
})
export class AppModule { }
